import { useEffect, useRef, useState } from 'react';
import { SkuSearchResult } from 'src/API';
import { searchSkuAPI } from 'src/services/api/skuSearchService';
import { usePermissions } from 'src/hooks/usePermissions';
import * as permissionsUtils from 'src/utils/permissionsUtils';
import { buildFpnPath, buildRpnPath } from 'src/components/manage-programs/utils/manageProgramsPathUtils';
import { logger } from 'src/utils/logger';
import { SearchResult, SearchResultSection } from 'src/types/search/search.types';

const ASIN_PATTERN = /^[Bb][0-9A-Za-z]{9}$/;
const PART_NUMBER_PATTERN = /^[A-Za-z0-9]+[-_.][A-Za-z0-9\-_.]{2,}$/;
const DEBOUNCE_MS = 300;

/**
 * Returns true if the query looks like an ASIN or part number worth searching.
 */
const isSearchable = (q: string): boolean => {
  return ASIN_PATTERN.test(q) || PART_NUMBER_PATTERN.test(q);
};

/**
 * Hook that detects ASIN/part number queries and performs a server-side search.
 */
interface SplitResults {
  asinResults: SearchResult[];
  partNumberResults: SearchResult[];
}

export const useSkuSearch = (query: string): { skuResults: SearchResultSection[]; isSearching: boolean } => {
  const { userPermission } = usePermissions();
  const [splitResults, setSplitResults] = useState<SplitResults>({ asinResults: [], partNumberResults: [] });
  const [isSearching, setIsSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const trimmed = query.trim();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isSearchable(trimmed)) {
      setSplitResults({ asinResults: [], partNumberResults: [] });
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    let cancelled = false;

    timerRef.current = setTimeout(async () => {
      try {
        const items = await searchSkuAPI(trimmed);
        if (cancelled) return;
        setSplitResults(mapResults(items, trimmed.toUpperCase(), userPermission));
      } catch (error) {
        if (!cancelled) {
          logger.error('useSkuSearch: Search failed', { error });
          setSplitResults({ asinResults: [], partNumberResults: [] });
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [query, userPermission]);

  const skuResults: SearchResultSection[] = [];
  if (splitResults.asinResults.length > 0) {
    skuResults.push({ title: 'ASINs', results: splitResults.asinResults });
  }
  if (splitResults.partNumberResults.length > 0) {
    skuResults.push({ title: 'Part Numbers', results: splitResults.partNumberResults });
  }

  return { skuResults, isSearching };
};

function mapResults(
  items: SkuSearchResult[],
  normalizedSearchTerm: string,
  userPermission: ReturnType<typeof usePermissions>['userPermission']
): SplitResults {
  const asinResults: SearchResult[] = [];
  const partNumberResults: SearchResult[] = [];

  for (const item of items) {
    if (
      item.is_confidential &&
      !permissionsUtils.canAccessConfidentialRPN(userPermission, item.product_line_id ?? 0, true)
    ) {
      continue;
    }

    if (!item.rpn_display_id || !item.product_code) {
      continue;
    }

    const isConfidential = item.is_confidential ?? false;
    const hasFpn = !!item.fpn_display_id && !!item.fpn_id;
    const type: SearchResult['type'] = hasFpn ? 'fpn' : 'rpn';
    const label = hasFpn ? (item.fpn_alias ?? 'Unknown FPN') : (item.rpn_alias ?? 'Unknown RPN');
    const breadcrumb = [
      `${item.product_line_name ?? ''} (${item.product_code})`,
      ...(hasFpn ? [item.rpn_alias ?? ''] : []),
    ];
    const path = hasFpn
      ? buildFpnPath(item.product_code, item.rpn_display_id, item.fpn_display_id!)
      : buildRpnPath(item.product_code, item.rpn_display_id);

    if (item.asin === normalizedSearchTerm) {
      asinResults.push({
        type,
        label,
        displayId: item.asin,
        breadcrumb,
        path,
        isConfidential,
      });
    }

    if (item.part_number === normalizedSearchTerm) {
      partNumberResults.push({
        type,
        label,
        displayId: item.part_number,
        breadcrumb,
        path,
        isConfidential,
      });
    }
  }

  return { asinResults, partNumberResults };
}
